// ─── ADMIN: AFFILIATE MANAGEMENT ──────────────────────────────────────────────
// Add this as a section in your MyProfile/Settings or as a separate admin tab
// Only visible to admin users (your email)
import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc
} from 'firebase/firestore';
import {
  createAffiliate, buildReferralLink,
  DEFAULT_COMMISSION_STRUCTURE, calculateAffiliateEarnings,
} from '../utils/referral';
import { getOperatingCostConfig, clearOperatingCostCache } from '../utils/planUtils';

const ADMIN_EMAIL = 'tracksysales@gmail.com'; // Your admin email

export default function AdminAffiliatePanel({ userEmail }) {
  const [affiliates, setAffiliates] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [copied, setCopied] = useState('');
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);

  // Commission editor state
  const [editingCommission, setEditingCommission] = useState(null); // affiliateId or null
  const [commissionDraft, setCommissionDraft] = useState(null);     // { rates }
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});                 // { [affiliateId]: { ok, msg } }

  // Operating cost state
  const [opConfig, setOpConfig] = useState({ monthlyOperatingCost: 40, leeway: 1 });
  const [editingOpCost, setEditingOpCost] = useState(false);
  const [opCostDraft, setOpCostDraft] = useState({ monthlyOperatingCost: 40, leeway: 1 });
  const [opCostSaving, setOpCostSaving] = useState(false);
  const [opCostStatus, setOpCostStatus] = useState(null); // { ok, msg }

  // New affiliate form
  const [newAffiliate, setNewAffiliate] = useState({
    name: '', email: '', phone: '', referralCode: '',
  });

  const isAdmin = userEmail === ADMIN_EMAIL;

  useEffect(() => {
    let cancelled = false;
    if (isAdmin) loadData(cancelled);
    return () => { cancelled = true; };
  }, [isAdmin]);

  const loadData = async (cancelled) => {
    setLoading(true);

    // Run migration server-side via Admin SDK (bypasses Firestore security rules)
    try {
      console.log('[AdminPanel] starting affiliate migration via /api/migrate-affiliates...');
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('No auth token — currentUser is null');
      const res = await fetch('/api/migrate-affiliates', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || `HTTP ${res.status}`);
      console.log(`[AdminPanel] migration complete — migrated ${result.migrated} of ${result.total} affiliate(s)`);
    } catch (err) {
      console.error('[AdminPanel] migration error:', err);
    }

    // Load operating cost config (fresh fetch on each admin panel load)
    clearOperatingCostCache();
    try {
      const cfg = await getOperatingCostConfig();
      if (!cancelled) setOpConfig(cfg);
    } catch {}

    try {
      const pSnap = await getDocs(collection(db, 'affiliates'));
      if (cancelled) return;
      const pList = pSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAffiliates(pList);

      const rSnap = await getDocs(collection(db, 'referrals'));
      if (cancelled) return;
      const rList = rSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReferrals(rList);
    } catch (err) {
      // Collections may not exist yet — fail silently
    }
    if (!cancelled) setLoading(false);
  };

  const handleAddAffiliate = async () => {
    if (!newAffiliate.name || !newAffiliate.email) return;
    // Use email as a simple userId for the affiliate doc
    const affiliateId = newAffiliate.email.replace(/[^a-zA-Z0-9]/g, '_');
    const code = await createAffiliate(affiliateId, newAffiliate);
    if (code) {
      setShowAddForm(false);
      setNewAffiliate({ name: '', email: '', phone: '', referralCode: '' });
      loadData();
    }
  };

  const toggleAffiliateActive = async (affiliateId, currentStatus) => {
    await updateDoc(doc(db, 'affiliates', affiliateId), { isActive: !currentStatus });
    loadData();
  };

  const handleDeleteAffiliate = async (affiliateId) => {
    if (!window.confirm('Are you sure you want to delete this affiliate?')) return;
    try {
      await deleteDoc(doc(db, 'affiliates', affiliateId));
      loadData();
    } catch (err) {
      // Fail silently
    }
  };

  const openCommissionEditor = (affiliate) => {
    const structure = affiliate.commissionStructure || DEFAULT_COMMISSION_STRUCTURE;
    setCommissionDraft({
      rates: {
        coreMonthly: Math.round(structure.coreMonthly * 100),
        proMonthly:  Math.round(structure.proMonthly  * 100),
        coreYearly:  Math.round(structure.coreYearly  * 100),
        proYearly:   Math.round(structure.proYearly   * 100),
      },
    });
    setEditingCommission(affiliate.id);
    setExpandedAffiliate(affiliate.id); // auto-expand so the editor is visible
  };

  const callCommissionApi = async (affiliateId, body) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('No auth token');
    const res = await fetch('/api/update-affiliate-commission', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ affiliateId, ...body }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const handleSaveCommission = async (affiliateId) => {
    if (!commissionDraft) return;
    setCommissionSaving(true);
    setSaveStatus(prev => ({ ...prev, [affiliateId]: null }));
    try {
      const commissionStructure = {};
      ['coreMonthly', 'proMonthly', 'coreYearly', 'proYearly'].forEach(key => {
        commissionStructure[key] = Math.min(1, Math.max(0, parseFloat(commissionDraft.rates[key]) / 100));
      });
      await callCommissionApi(affiliateId, { commissionStructure });
      setSaveStatus(prev => ({ ...prev, [affiliateId]: { ok: true, msg: '✅ Saved to Firestore' } }));
      setEditingCommission(null);
      setCommissionDraft(null);
      loadData();
    } catch (err) {
      console.error('[AdminAffiliatePanel] saveCommission error:', err);
      setSaveStatus(prev => ({ ...prev, [affiliateId]: { ok: false, msg: `❌ Save failed: ${err.message}` } }));
    }
    setCommissionSaving(false);
  };

  const handleResetCommission = async (affiliateId) => {
    setCommissionSaving(true);
    setSaveStatus(prev => ({ ...prev, [affiliateId]: null }));
    try {
      await callCommissionApi(affiliateId, { reset: true });
      setSaveStatus(prev => ({ ...prev, [affiliateId]: { ok: true, msg: '✅ Reset to defaults' } }));
      setEditingCommission(null);
      setCommissionDraft(null);
      loadData();
    } catch (err) {
      console.error('[AdminAffiliatePanel] resetCommission error:', err);
      setSaveStatus(prev => ({ ...prev, [affiliateId]: { ok: false, msg: `❌ Reset failed: ${err.message}` } }));
    }
    setCommissionSaving(false);
  };

  const handleSaveOpCost = async () => {
    setOpCostSaving(true);
    setOpCostStatus(null);
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      setOpCostStatus({ ok: false, msg: '❌ No auth token' });
      setOpCostSaving(false);
      return;
    }
    try {
      const body = {
        monthlyOperatingCost: parseFloat(opCostDraft.monthlyOperatingCost) || 40,
        leeway: parseFloat(opCostDraft.leeway) ?? 1,
      };
      const res = await fetch('/api/update-operating-cost', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      clearOperatingCostCache();
      setOpConfig(body);
      setEditingOpCost(false);
      setOpCostStatus({
        ok: true,
        msg: `✅ Operating cost updated — recalculated commission for ${data.affiliatesUpdated} affiliates across ${data.referralsUpdated} referrals`,
      });
      loadData(); // refresh affiliate list
    } catch (err) {
      setOpCostStatus({ ok: false, msg: `❌ Save failed: ${err.message}` });
    }
    setOpCostSaving(false);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isAdmin) return null;

  const getAffiliateReferrals = (affiliateId) => referrals.filter(r => r.affiliateId === affiliateId);

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h3 style={s.title}>🤝 Affiliate Management</h3>
        <button onClick={() => setShowAddForm(!showAddForm)} style={s.addBtn}>
          {showAddForm ? '✕ Cancel' : '+ Add Affiliate'}
        </button>
      </div>

      {/* ADD AFFILIATE FORM */}
      {showAddForm && (
        <div style={s.formCard}>
          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.label}>Affiliate Name *</label>
              <input
                value={newAffiliate.name}
                onChange={e => setNewAffiliate({ ...newAffiliate, name: e.target.value })}
                style={s.input}
                placeholder="John Smith"
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Email *</label>
              <input
                type="email"
                value={newAffiliate.email}
                onChange={e => setNewAffiliate({ ...newAffiliate, email: e.target.value })}
                style={s.input}
                placeholder="affiliate@email.com"
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Phone</label>
              <input
                value={newAffiliate.phone}
                onChange={e => setNewAffiliate({ ...newAffiliate, phone: e.target.value })}
                style={s.input}
                placeholder="(555) 123-4567"
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Custom Referral Code (optional)</label>
              <input
                value={newAffiliate.referralCode}
                onChange={e => setNewAffiliate({ ...newAffiliate, referralCode: e.target.value.toUpperCase() })}
                style={s.input}
                placeholder="Auto-generated if blank"
                maxLength={12}
              />
            </div>
          </div>
          <button onClick={handleAddAffiliate} style={s.saveBtn}>
            ✅ Create Affiliate
          </button>
        </div>
      )}

      {/* SUMMARY STATS */}
      <div style={s.summaryGrid}>
        <div style={s.summaryCard}>
          <span style={s.summaryNum}>{affiliates.length}</span>
          <span style={s.summaryLabel}>Affiliates</span>
        </div>
        <div style={s.summaryCard}>
          <span style={{ ...s.summaryNum, color: '#00ff88' }}>{referrals.length}</span>
          <span style={s.summaryLabel}>Referred Subscribers</span>
        </div>
        <div style={s.summaryCard}>
          <span style={{ ...s.summaryNum, color: '#00ccff' }}>
            {referrals.filter(r => r.status === 'active').length}
          </span>
          <span style={s.summaryLabel}>Active Referred Subs</span>
        </div>
        <div style={s.summaryCard}>
          <span style={{ ...s.summaryNum, color: '#ffaa00' }}>
            {referrals.filter(r => r.status === 'trial').length}
          </span>
          <span style={s.summaryLabel}>In Trial</span>
        </div>
      </div>

      {/* OPERATING COST CONFIG */}
      <div style={s.opCostCard}>
        <div style={s.opCostRow}>
          <div>
            <span style={s.miniLabel}>Monthly Operating Cost</span>
            {!editingOpCost ? (
              <span style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginLeft: 10 }}>
                ${opConfig.monthlyOperatingCost.toFixed(2)}/mo
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', marginLeft: 8 }}>
                  (leeway: ${Number(opConfig.leeway).toFixed(2)})
                </span>
              </span>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                <div style={s.commInputGroup}>
                  <span style={s.commInputLabel}>Operating Cost $</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={opCostDraft.monthlyOperatingCost}
                    onChange={e => setOpCostDraft(prev => ({ ...prev, monthlyOperatingCost: e.target.value }))}
                    style={{ ...s.commInput, width: 80 }}
                  />
                </div>
                <div style={s.commInputGroup}>
                  <span style={s.commInputLabel}>Leeway $</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={opCostDraft.leeway}
                    onChange={e => setOpCostDraft(prev => ({ ...prev, leeway: e.target.value }))}
                    style={{ ...s.commInput, width: 64 }}
                  />
                </div>
                <button onClick={handleSaveOpCost} disabled={opCostSaving} style={s.commSaveBtn}>
                  {opCostSaving ? 'Saving...' : '💾 Save'}
                </button>
                <button onClick={() => setEditingOpCost(false)} style={s.commResetBtn}>
                  Cancel
                </button>
              </div>
            )}
          </div>
          {!editingOpCost && (
            <button
              onClick={() => {
                setOpCostDraft({ ...opConfig });
                setEditingOpCost(true);
                setOpCostStatus(null);
              }}
              style={s.editRatesBtn}
            >
              ✏️ Edit Cost
            </button>
          )}
        </div>
        {opCostStatus && (
          <p style={{
            marginTop: 8, fontSize: 12, fontWeight: 700,
            color: opCostStatus.ok ? '#00ff88' : '#f87171',
          }}>
            {opCostStatus.msg}
          </p>
        )}
      </div>

      {/* AFFILIATES LIST */}
      {loading ? (
        <p style={{ color: 'rgba(255,255,255,.4)', textAlign: 'center', padding: 20 }}>Loading...</p>
      ) : affiliates.length === 0 ? (
        <div style={s.emptyState}>
          <span style={{ fontSize: 40 }}>🤝</span>
          <p style={{ color: 'rgba(255,255,255,.5)', marginTop: 12 }}>No affiliates yet. Add your first affiliate above.</p>
        </div>
      ) : (
        <div style={s.affiliateList}>
          {affiliates.map(p => {
            const pRefs = getAffiliateReferrals(p.id);
            const activeRefs = pRefs.filter(r => r.status === 'active');
            const isExpanded = expandedAffiliate === p.id;
            const link = buildReferralLink(p.referralCode);

            return (
              <div key={p.id} style={s.affiliateCard}>
                {/* Affiliate Header */}
                <div
                  style={s.affiliateHeader}
                  onClick={() => setExpandedAffiliate(isExpanded ? null : p.id)}
                >
                  <div>
                    <div style={s.affiliateNameRow}>
                      <span style={s.affiliateName}>{p.name}</span>
                      <span style={{
                        ...s.statusDot,
                        background: p.isActive ? '#00ff88' : '#ff4d4d'
                      }} />
                    </div>
                    <p style={s.affiliateEmail}>{p.email}</p>
                  </div>
                  <div style={s.affiliateQuickStats}>
                    <span style={s.quickStat}>
                      <strong style={{ color: '#00ff88' }}>{activeRefs.length}</strong> active referred
                    </span>
                    <span style={s.quickStat}>
                      <strong>{pRefs.length}</strong> total referred
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editingCommission === p.id
                          ? setEditingCommission(null)
                          : openCommissionEditor(p);
                      }}
                      style={s.editRatesBtn}
                    >
                      ✏️ {editingCommission === p.id ? 'Close Rates' : 'Edit Rates'}
                    </button>
                    <span style={{ fontSize: 16, color: 'rgba(255,255,255,.3)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s' }}>▼</span>
                  </div>
                </div>

                {/* Commission Editor — rendered independently of expand state */}
                {editingCommission === p.id && commissionDraft && (
                  <div style={s.commEditor}>
                    <span style={{ ...s.miniLabel, marginBottom: 10, display: 'block' }}>
                      Commission Rates
                    </span>
                    <div style={s.commGrid}>
                      {[
                        { key: 'coreMonthly', label: 'Core Monthly' },
                        { key: 'proMonthly',  label: 'Pro Monthly'  },
                        { key: 'coreYearly',  label: 'Core Yearly'  },
                        { key: 'proYearly',   label: 'Pro Yearly'   },
                      ].map(({ key, label }) => (
                        <div key={key} style={s.commRow}>
                          <span style={s.commRowLabel}>{label}</span>
                          <div style={s.commInputPair}>
                            <div style={s.commInputGroup}>
                              <span style={s.commInputLabel}>Rate %</span>
                              <input
                                type="number"
                                min="0" max="100"
                                value={commissionDraft.rates[key]}
                                onChange={e => setCommissionDraft(prev => ({
                                  ...prev,
                                  rates: { ...prev.rates, [key]: e.target.value },
                                }))}
                                style={s.commInput}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p style={s.commNote}>
                      ⚠️ Changes apply to future payments only — past commissions are not recalculated.
                    </p>
                    <div style={s.commActions}>
                      <button
                        onClick={() => handleSaveCommission(p.id)}
                        disabled={commissionSaving}
                        style={s.commSaveBtn}
                      >
                        {commissionSaving ? 'Saving...' : '💾 Save Rates'}
                      </button>
                      <button
                        onClick={() => handleResetCommission(p.id)}
                        disabled={commissionSaving}
                        style={s.commResetBtn}
                      >
                        ↩ Reset to Defaults
                      </button>
                    </div>
                    {saveStatus[p.id] && (
                      <p style={{
                        marginTop: 8, fontSize: 12, fontWeight: 700,
                        color: saveStatus[p.id].ok ? '#00ff88' : '#f87171',
                      }}>
                        {saveStatus[p.id].msg}
                      </p>
                    )}
                  </div>
                )}

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={s.affiliateExpanded}>
                    {/* Link + Code */}
                    <div style={s.linkRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={s.miniLabel}>Referral Code</span>
                        <span style={s.codeDisplay}>{p.referralCode}</span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(link, p.id)}
                        style={s.miniCopyBtn}
                      >
                        {copied === p.id ? '✅' : '📋'} Copy Link
                      </button>
                    </div>
                    <p style={s.linkPreview}>{link}</p>

                    {/* Revenue Progress Bar */}
                    {(() => {
                      const earnings = calculateAffiliateEarnings(p, opConfig.monthlyOperatingCost, opConfig.leeway);
                      const pct = earnings.effectiveThreshold > 0
                        ? Math.min(100, (earnings.totalMonthlyRevenue / earnings.effectiveThreshold) * 100)
                        : 100;
                      return (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={s.miniLabel}>Monthly Revenue vs Operating Cost</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: earnings.thresholdMet ? '#00ff88' : '#ffaa00' }}>
                              ${earnings.totalMonthlyRevenue.toFixed(2)} / ${earnings.effectiveThreshold.toFixed(2)}
                            </span>
                          </div>
                          <div style={{ height: 8, background: 'rgba(255,255,255,.08)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', width: `${pct}%`,
                              background: earnings.thresholdMet ? '#00ff88' : '#ffaa00',
                              borderRadius: 4, transition: 'width .3s',
                            }} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', fontWeight: 600 }}>
                              {earnings.subscribersCoveringThreshold} covering costs · {earnings.subscribersEarningCommission} earning commission
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 800, color: earnings.totalMonthlyCommission > 0 ? '#00ff88' : 'rgba(255,255,255,.35)' }}>
                              ${earnings.totalMonthlyCommission.toFixed(2)}/mo owed
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            {['coreMonthly', 'proMonthly', 'coreYearly', 'proYearly'].map(key => {
                              const labels = { coreMonthly: 'Core Mo.', proMonthly: 'Pro Mo.', coreYearly: 'Core Yr.', proYearly: 'Pro Yr.' };
                              const count = p[`${key}Count`] || 0;
                              return (
                                <div key={key} style={s.rateBox}>
                                  <span style={s.rateLabel}>{labels[key]}</span>
                                  <span style={s.rateValue}>{count}</span>
                                </div>
                              );
                            })}
                            <div style={s.rateBox}>
                              <span style={s.rateLabel}>Since</span>
                              <span style={s.rateValue}>{formatDate(p.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Their Referrals */}
                    {pRefs.length > 0 && (
                      <div style={s.refList}>
                        <span style={s.miniLabel}>Referred Subscribers</span>
                        {pRefs.map(r => (
                          <div key={r.id} style={s.refRow}>
                            <span style={s.refEmail}>{r.userEmail}</span>
                            <span style={{
                              ...s.refStatus,
                              color: r.status === 'active' ? '#00ff88' : r.status === 'trial' ? '#ffaa00' : '#ff4d4d'
                            }}>
                              {r.status}
                            </span>
                            <span style={s.refDate}>{formatDate(r.signupDate)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div style={s.actionRow}>
                      <button
                        onClick={() => toggleAffiliateActive(p.id, p.isActive)}
                        style={{
                          ...s.actionBtn,
                          borderColor: p.isActive ? 'rgba(255,77,77,.25)' : 'rgba(0,255,136,.25)',
                          color: p.isActive ? '#ff4d4d' : '#00ff88',
                        }}
                      >
                        {p.isActive ? '⏸ Deactivate' : '▶ Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteAffiliate(p.id)}
                        style={{
                          ...s.actionBtn,
                          borderColor: 'rgba(255,77,77,.25)',
                          color: '#ff4d4d',
                        }}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── STYLES ─────────────────────────────────────────────────────────────────
const s = {
  container: { marginTop: 20 },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
  },
  title: { fontSize: 17, fontWeight: 800, color: '#fff', margin: 0 },
  addBtn: {
    padding: '8px 16px', borderRadius: 10, border: '1px solid rgba(0,255,136,.3)',
    background: 'rgba(0,255,136,.1)', color: '#00ff88', fontSize: 13,
    fontWeight: 700, cursor: 'pointer',
  },

  // Form
  formCard: {
    padding: 18, borderRadius: 14, background: 'rgba(255,255,255,.04)',
    border: '1px solid rgba(0,255,136,.15)', marginBottom: 16,
  },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.45)', textTransform: 'uppercase', letterSpacing: 0.8 },
  input: {
    padding: '11px 14px', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
    borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
  },
  saveBtn: {
    width: '100%', marginTop: 14, padding: '14px', borderRadius: 12, border: 'none',
    background: 'linear-gradient(135deg, #00ff88, #00ccff)', color: '#000',
    fontSize: 14, fontWeight: 800, cursor: 'pointer',
  },

  // Summary
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 },
  summaryCard: {
    padding: '14px 10px', borderRadius: 12, background: 'rgba(255,255,255,.03)',
    border: '1px solid rgba(255,255,255,.06)', textAlign: 'center',
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  summaryNum: { fontSize: 22, fontWeight: 900, color: '#fff' },
  summaryLabel: { fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: 0.5 },

  // Empty
  emptyState: { textAlign: 'center', padding: '32px 20px' },

  // Affiliate List
  opCostCard: {
    padding: '14px 16px', borderRadius: 12,
    background: 'rgba(255,255,255,.03)', border: '1px solid rgba(0,204,255,.12)',
    marginBottom: 14,
  },
  opCostRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
  },
  affiliateList: { display: 'flex', flexDirection: 'column', gap: 10 },
  affiliateCard: {
    borderRadius: 14, background: 'rgba(255,255,255,.03)',
    border: '1px solid rgba(255,255,255,.06)', overflow: 'hidden',
  },
  affiliateHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px', cursor: 'pointer', transition: 'background .15s',
  },
  affiliateNameRow: { display: 'flex', alignItems: 'center', gap: 8 },
  affiliateName: { fontSize: 15, fontWeight: 700, color: '#fff' },
  statusDot: { width: 8, height: 8, borderRadius: '50%' },
  affiliateEmail: { fontSize: 12, color: 'rgba(255,255,255,.4)', marginTop: 2 },
  affiliateQuickStats: { display: 'flex', alignItems: 'center', gap: 12 },
  quickStat: { fontSize: 12, color: 'rgba(255,255,255,.5)' },

  // Expanded
  affiliateExpanded: {
    padding: '0 16px 16px', borderTop: '1px solid rgba(255,255,255,.06)',
  },
  linkRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 14, gap: 10,
  },
  miniLabel: {
    display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.35)',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
  },
  codeDisplay: { fontSize: 16, fontWeight: 800, color: '#00ccff', letterSpacing: 2 },
  miniCopyBtn: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(0,255,136,.25)',
    background: 'rgba(0,255,136,.08)', color: '#00ff88', fontSize: 12,
    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  linkPreview: {
    fontSize: 11, color: 'rgba(0,255,136,.5)', marginTop: 6,
    wordBreak: 'break-all', fontFamily: 'monospace',
  },
  ratesRow: { display: 'flex', gap: 10, marginTop: 14 },
  rateBox: {
    flex: 1, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,.04)',
    border: '1px solid rgba(255,255,255,.06)', textAlign: 'center',
  },
  rateLabel: { display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  rateValue: { fontSize: 14, fontWeight: 800, color: '#fff' },

  refList: { marginTop: 14 },
  refRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
    background: 'rgba(255,255,255,.02)', borderRadius: 8, marginTop: 6,
    border: '1px solid rgba(255,255,255,.04)',
  },
  refEmail: { flex: 1, fontSize: 13, color: 'rgba(255,255,255,.7)', fontWeight: 600 },
  refStatus: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 },
  refDate: { fontSize: 11, color: 'rgba(255,255,255,.3)' },

  editRatesBtn: {
    padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(0,204,255,.3)',
    background: 'rgba(0,204,255,.08)', color: '#00ccff',
    fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  },

  actionRow: { display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' },
  actionBtn: {
    padding: '10px 18px', borderRadius: 10, background: 'transparent',
    border: '1px solid', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },

  // Commission editor
  commEditor: {
    marginTop: 14, padding: '14px 16px', borderRadius: 12,
    background: 'rgba(0,204,255,.04)', border: '1px solid rgba(0,204,255,.15)',
  },
  commGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
  commRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12,
  },
  commRowLabel: { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.7)', minWidth: 90 },
  commInputPair: { display: 'flex', gap: 8 },
  commInputGroup: { display: 'flex', flexDirection: 'column', gap: 3 },
  commInputLabel: {
    fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,.35)',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  commInput: {
    width: 64, padding: '7px 10px', background: 'rgba(255,255,255,.06)',
    border: '1px solid rgba(255,255,255,.1)', borderRadius: 8,
    color: '#fff', fontSize: 13, fontWeight: 700, outline: 'none',
    textAlign: 'center',
  },
  commNote: {
    fontSize: 11, color: 'rgba(255,170,0,.8)', marginTop: 10, marginBottom: 0,
    lineHeight: 1.5,
  },
  commActions: { display: 'flex', gap: 8, marginTop: 10 },
  commSaveBtn: {
    padding: '9px 18px', borderRadius: 9, border: 'none',
    background: 'linear-gradient(135deg, #00ff88, #00ccff)', color: '#000',
    fontSize: 13, fontWeight: 800, cursor: 'pointer',
  },
  commResetBtn: {
    padding: '9px 16px', borderRadius: 9,
    border: '1px solid rgba(255,255,255,.15)', background: 'transparent',
    color: 'rgba(255,255,255,.5)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
};
